import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Loader2,
  ArrowRight,
  Briefcase,
  X as XIcon,
  FileText,
  Paperclip,
  Plus,
  Search,
  Upload,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import type { AttachedDocument } from '../../services/smartStartApi';
import { fetchProjectIngestedDocuments } from '../../services/smartStartApi';
import { useProject } from '../../contexts/ProjectContext';
import CreateProjectModal from '../CreateProjectModal';
import PipelinePicker from './PipelinePicker';

interface Props {
  isSubmitting: boolean;
  onSubmit: (
    goal: string,
    projectId: number | null,
    attachedDocuments: AttachedDocument[]
  ) => void;
  onPipelineLaunch?: (
    pipelineKey: string,
    name: string,
    attachedDocuments: AttachedDocument[]
  ) => void | Promise<void>;
}

const MAX_FILES = 10;
const PENDING_GOAL_KEY = 'smartStartPendingGoal';

interface PendingGoalDraft {
  goal: string;
  projectId: number | null;
  attachedDocs: AttachedDocument[];
  pendingFetchProjectDocs: boolean;
  savedAt: string;
}

const GoalEntry: React.FC<Props> = ({ isSubmitting, onSubmit, onPipelineLaunch }) => {
  const navigate = useNavigate();
  const { projects, currentProject, setCurrentProject } = useProject();

  const [goal, setGoal] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    currentProject?.id ?? null
  );
  const [attachedDocs, setAttachedDocs] = useState<AttachedDocument[]>([]);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [goalPulse, setGoalPulse] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [projectDocs, setProjectDocs] = useState<AttachedDocument[]>([]);
  const [projectDocsLoading, setProjectDocsLoading] = useState(false);
  const [projectDocsError, setProjectDocsError] = useState<string | null>(null);
  const [docSearch, setDocSearch] = useState('');

  const restoredFromDraft = useRef(false);
  useEffect(() => {
    if (restoredFromDraft.current) return;
    restoredFromDraft.current = true;
    let pendingProjectIdForFetch: number | null = null;
    try {
      const raw = localStorage.getItem(PENDING_GOAL_KEY);
      if (!raw) return;
      const draft: PendingGoalDraft = JSON.parse(raw);
      if (!draft || typeof draft !== 'object') return;
      if (typeof draft.goal === 'string') setGoal(draft.goal);
      if (typeof draft.projectId === 'number' || draft.projectId === null) {
        setSelectedProjectId(draft.projectId);
      }
      if (Array.isArray(draft.attachedDocs)) setAttachedDocs(draft.attachedDocs);
      if (draft.pendingFetchProjectDocs && typeof draft.projectId === 'number') {
        pendingProjectIdForFetch = draft.projectId;
      }
    } catch (_) {
    } finally {
      localStorage.removeItem(PENDING_GOAL_KEY);
    }
    if (pendingProjectIdForFetch !== null) {
      const projectId = pendingProjectIdForFetch;
      fetchProjectIngestedDocuments(projectId)
        .then((docs) => {
          setProjectDocs(Array.isArray(docs) ? docs : []);
          if (Array.isArray(docs) && docs.length > 0) {
            toast.success(
              `Welcome back — ${docs.length} document${docs.length === 1 ? ' is' : 's are'} ready in this project. Tick the ones you want to attach.`,
              { duration: 4500 }
            );
          }
        })
        .catch(() => {});
    }
  }, []);

  const lastCurrentProjectId = useRef<number | null>(currentProject?.id ?? null);
  useEffect(() => {
    const cpId = currentProject?.id ?? null;
    if (cpId !== lastCurrentProjectId.current) {
      lastCurrentProjectId.current = cpId;
      if (cpId !== null) setSelectedProjectId(cpId);
    } else if (cpId !== null && selectedProjectId === null) {
      setSelectedProjectId(cpId);
    }
  }, [currentProject, selectedProjectId]);

  const selectedProject = useMemo(
    () => (selectedProjectId ? projects.find((p) => p.id === selectedProjectId) || null : null),
    [selectedProjectId, projects]
  );

  const handleSelectProject = (id: number | null) => {
    setSelectedProjectId(id);
    if (id) {
      const proj = projects.find((p) => p.id === id) || null;
      if (proj) setCurrentProject(proj);
    }
  };

  const loadProjectDocs = useCallback(
    async (projectId: number) => {
      setProjectDocsLoading(true);
      setProjectDocsError(null);
      try {
        const docs = await fetchProjectIngestedDocuments(projectId);
        setProjectDocs(Array.isArray(docs) ? docs : []);
      } catch (err: any) {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load project documents';
        setProjectDocsError(msg);
        setProjectDocs([]);
      } finally {
        setProjectDocsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (selectedProjectId == null) {
      setProjectDocs([]);
      return;
    }
    void loadProjectDocs(selectedProjectId);
  }, [selectedProjectId, loadProjectDocs]);

  const toggleAttachDoc = (doc: AttachedDocument) => {
    setAttachedDocs((prev) => {
      const isAttached = prev.some((d) => d.name === doc.name);
      if (isAttached) return prev.filter((d) => d.name !== doc.name);
      if (prev.length >= MAX_FILES) {
        toast.error(`You can attach at most ${MAX_FILES} documents.`);
        return prev;
      }
      return [...prev, doc];
    });
  };

  const persistDraftAndNavigate = (target: string, opts?: { autoAttachOnReturn?: boolean }) => {
    const draft: PendingGoalDraft = {
      goal,
      projectId: selectedProjectId,
      attachedDocs,
      pendingFetchProjectDocs: !!opts?.autoAttachOnReturn,
      savedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(PENDING_GOAL_KEY, JSON.stringify(draft));
    } catch (_) {}
    navigate(target);
  };

  const handleGoToIngestion = () => {
    if (!selectedProjectId) {
      toast.error('Pick or create a project first — uploads need a project.');
      return;
    }
    persistDraftAndNavigate(`/app/documents?returnTo=smart-start&openUpload=true`, {
      autoAttachOnReturn: true,
    });
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!goal.trim() || isSubmitting) return;
    onSubmit(goal.trim(), selectedProjectId, attachedDocs);
  };

  const handlePipelinePick = async (pipelineKey: string, name: string) => {
    if (onPipelineLaunch) {
      await onPipelineLaunch(pipelineKey, name, attachedDocs);
      return;
    }
    setGoal(name);
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        node.focus({ preventScroll: true });
        const len = name.length;
        node.setSelectionRange(len, len);
      }
    });
    setGoalPulse(true);
    window.setTimeout(() => setGoalPulse(false), 1200);
    toast.success(`Loaded "${name}" — review and click Analyze when ready`, { duration: 3000 });
  };

  const formatSize = (bytes?: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const attachedNamesSet = useMemo(
    () => new Set(attachedDocs.map((d) => d.name)),
    [attachedDocs]
  );

  const filteredProjectDocs = useMemo(() => {
    const q = docSearch.trim().toLowerCase();
    if (!q) return projectDocs;
    return projectDocs.filter((d) => d.name.toLowerCase().includes(q));
  }, [projectDocs, docSearch]);

  const projectButtonLabel = selectedProject
    ? selectedProject.name
    : 'Pick a project';

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">What do you want to do?</h1>
          <p className="text-sm text-gray-500 mt-1">
            Describe it, or pick a workflow below.
          </p>
        </div>

        <div
          className={`relative bg-white rounded-2xl border shadow-sm transition-all ${
            goalPulse
              ? 'border-lavender ring-2 ring-lavender/30 bg-lavender/5'
              : 'border-gray-200 focus-within:border-lavender focus-within:ring-2 focus-within:ring-lavender/20'
          }`}
        >
          <textarea
            ref={textareaRef}
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            placeholder="e.g. Build an SOP for our vendor onboarding process…"
            className="w-full p-4 pb-2 text-base bg-transparent border-0 focus:outline-none resize-none rounded-2xl placeholder:text-gray-400"
            disabled={isSubmitting}
          />

          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100">
            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
              <ProjectChipButton
                label={projectButtonLabel}
                active={!!selectedProject}
                disabled={isSubmitting}
                onPick={(id) => handleSelectProject(id)}
                onCreate={() => setShowCreateProject(true)}
                projects={projects}
                selectedProjectId={selectedProjectId}
              />
              <AttachChipButton
                attachedCount={attachedDocs.length}
                projectSelected={!!selectedProjectId}
                disabled={isSubmitting}
                projectName={selectedProject?.name || null}
                projectDocs={projectDocs}
                attachedNamesSet={attachedNamesSet}
                projectDocsLoading={projectDocsLoading}
                projectDocsError={projectDocsError}
                docSearch={docSearch}
                setDocSearch={setDocSearch}
                filteredProjectDocs={filteredProjectDocs}
                onToggleDoc={toggleAttachDoc}
                onRefresh={() => { if (selectedProjectId) void loadProjectDocs(selectedProjectId); }}
                onUploadNew={handleGoToIngestion}
                formatSize={formatSize}
              />
            </div>
            <button
              type="submit"
              disabled={!goal.trim() || isSubmitting}
              title="Analyze (⌘/Ctrl + Enter)"
              className="flex items-center gap-1.5 px-4 py-1.5 bg-lavender text-white text-sm font-semibold rounded-lg hover:bg-lavender-deep transition disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Analyze
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>

        {attachedDocs.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {attachedDocs.map((d) => (
              <span
                key={d.name}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs text-emerald-900 max-w-[260px]"
                title={`${d.name}${d.size ? ` · ${formatSize(d.size)}` : ''}`}
              >
                <FileText className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                <span className="truncate">{d.name}</span>
                <button
                  type="button"
                  onClick={() => toggleAttachDoc(d)}
                  disabled={isSubmitting}
                  className="ml-0.5 p-0.5 text-emerald-700 hover:text-red-700 disabled:opacity-50"
                  aria-label={`Detach ${d.name}`}
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </form>

      <div className="max-w-5xl mx-auto">
        <PipelinePicker onPick={handlePipelinePick} />
      </div>

      <CreateProjectModal
        isOpen={showCreateProject}
        onClose={() => setShowCreateProject(false)}
      />
    </div>
  );
};

interface ProjectChipButtonProps {
  label: string;
  active: boolean;
  disabled: boolean;
  onPick: (id: number | null) => void;
  onCreate: () => void;
  projects: Array<{ id: number; name: string; client_name?: string | null }>;
  selectedProjectId: number | null;
}

const ProjectChipButton: React.FC<ProjectChipButtonProps> = ({
  label, active, disabled, onPick, onCreate, projects, selectedProjectId,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={active ? `Project: ${label}` : 'Pick a project (optional)'}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition disabled:opacity-50 ${
          active
            ? 'bg-lavender/10 border-lavender/40 text-lavender-deep'
            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
        }`}
      >
        <Briefcase className="w-3 h-3" />
        <span className="truncate max-w-[180px]">{label}</span>
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-30 overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 text-[10px] uppercase font-bold tracking-wide text-gray-500">
            Project
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            <li>
              <button
                type="button"
                onClick={() => { onPick(null); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${selectedProjectId == null ? 'text-lavender font-semibold' : 'text-gray-700'}`}
              >
                — No project —
              </button>
            </li>
            {projects.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => { onPick(p.id); setOpen(false); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                    selectedProjectId === p.id ? 'text-lavender font-semibold bg-lavender/5' : 'text-gray-700'
                  }`}
                >
                  <span className="truncate">{p.name}</span>
                  {p.client_name && (
                    <span className="text-gray-400"> · {p.client_name}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => { setOpen(false); onCreate(); }}
            className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-lavender border-t border-gray-100 hover:bg-lavender/5"
          >
            <Plus className="w-3.5 h-3.5" />
            Create new project
          </button>
        </div>
      )}
    </div>
  );
};

interface AttachChipButtonProps {
  attachedCount: number;
  projectSelected: boolean;
  disabled: boolean;
  projectName: string | null;
  projectDocs: AttachedDocument[];
  attachedNamesSet: Set<string>;
  projectDocsLoading: boolean;
  projectDocsError: string | null;
  docSearch: string;
  setDocSearch: (s: string) => void;
  filteredProjectDocs: AttachedDocument[];
  onToggleDoc: (d: AttachedDocument) => void;
  onRefresh: () => void;
  onUploadNew: () => void;
  formatSize: (bytes?: number | null) => string;
}

const AttachChipButton: React.FC<AttachChipButtonProps> = ({
  attachedCount, projectSelected, disabled, projectName, projectDocs, attachedNamesSet,
  projectDocsLoading, projectDocsError, docSearch, setDocSearch, filteredProjectDocs,
  onToggleDoc, onRefresh, onUploadNew, formatSize,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const active = attachedCount > 0;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        title={active ? `${attachedCount} attached` : 'Attach documents (optional)'}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition disabled:opacity-50 ${
          active
            ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
        }`}
      >
        <Paperclip className="w-3 h-3" />
        {active ? `${attachedCount} attached` : 'Attach'}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-96 bg-white rounded-xl shadow-lg border border-gray-200 z-30 overflow-hidden">
          {!projectSelected ? (
            <div className="p-4">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-900">
                  Pick a project first — documents are stored per project so they can be reused across runs.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                <div className="text-[10px] uppercase font-bold tracking-wide text-gray-500 truncate">
                  {projectName || 'Project'} · documents
                </div>
                <button
                  type="button"
                  onClick={onRefresh}
                  disabled={projectDocsLoading}
                  className="p-1 text-gray-500 hover:text-gray-800 disabled:opacity-50"
                  title="Reload"
                >
                  <RefreshCw className={`w-3 h-3 ${projectDocsLoading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <div className="px-3 py-2 border-b border-gray-100">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={docSearch}
                    onChange={(e) => setDocSearch(e.target.value)}
                    placeholder="Search documents…"
                    className="w-full pl-8 pr-2 py-1.5 text-xs rounded-md border border-gray-200 bg-white focus:border-lavender focus:ring-2 focus:ring-lavender/20 focus:outline-none"
                  />
                </div>
              </div>
              {projectDocsLoading ? (
                <div className="flex items-center justify-center gap-2 py-6 text-xs text-gray-500">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
                </div>
              ) : projectDocsError ? (
                <div className="p-3 text-xs text-red-700 bg-red-50">{projectDocsError}</div>
              ) : filteredProjectDocs.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-gray-500">
                  {docSearch.trim()
                    ? 'No documents match your search.'
                    : 'No documents in this project yet.'}
                </div>
              ) : (
                <ul className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {filteredProjectDocs.map((d) => {
                    const isAttached = attachedNamesSet.has(d.name);
                    const hasText = typeof d.text === 'string' && d.text.length > 0;
                    const atCap = !isAttached && attachedCount >= MAX_FILES;
                    return (
                      <li
                        key={d.name}
                        onClick={() => !atCap && onToggleDoc(d)}
                        className={`flex items-center gap-2 px-3 py-2 transition ${
                          atCap
                            ? 'opacity-50 cursor-not-allowed'
                            : isAttached
                              ? 'bg-emerald-50 cursor-pointer hover:bg-emerald-100/60'
                              : 'cursor-pointer hover:bg-gray-50'
                        }`}
                        title={
                          atCap ? `Limit ${MAX_FILES} reached` : isAttached ? 'Click to detach' : 'Click to attach'
                        }
                      >
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                            isAttached ? 'bg-emerald-500 border-emerald-500' : 'bg-white border-gray-300'
                          }`}
                        >
                          {isAttached && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-gray-900 truncate font-medium">{d.name}</div>
                          <div className="text-[10px] text-gray-500">
                            {formatSize(d.size)}
                            {hasText
                              ? ` · ${(d.text!.length / 1000).toFixed(1)}k chars`
                              : ' · extracting…'}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <button
                type="button"
                onClick={() => { setOpen(false); onUploadNew(); }}
                className="w-full flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-lavender border-t border-gray-100 hover:bg-lavender/5"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload a new document
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GoalEntry;
